import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  FormBuilder,
  ReactiveFormsModule,
  FormGroup,
  FormsModule,
  Validators,
} from '@angular/forms';
import { HeaderComponent } from '../../shared/header/header.component';
import { ActivatedRoute, Router } from '@angular/router';
import { AuthService } from '../../shared/services/auth.service';
import { QuoteService } from '../../shared/services/quote.service';
import { catchError, finalize, of, take } from 'rxjs';
import { LoaderComponent } from '../../components/loader/loader.component';
import { ToastrService } from 'ngx-toastr';
import {
  PaymentRequestDto,
  PaymentService,
} from '../../shared/services/payment.service';
import { Company } from '../../models/company.model';
import { CompanyService } from '../../shared/services/company.service';
import {
  ProviderQuote,
  QuotationDetail,
  QuoteRequest,
} from '../../models/quote-response.model';
import { ViewChild, ElementRef } from '@angular/core';

import {
  PaymentRequest,
  PaymentResponse,
} from '../../models/payment-request.interface';
import { TranslateModule } from '@ngx-translate/core';
import { StepperComponent } from "../../shared/stepper/stepper.component";
import { IssuePolicyRequest, PolicyService } from '../../shared/services/policy.service';

@Component({
  selector: 'app-payment',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    HeaderComponent,
    LoaderComponent,
    TranslateModule,
    ReactiveFormsModule,
    StepperComponent
],
  templateUrl: './payment.component.html',
  styleUrls: ['./payment.component.scss'],
})
export class PaymentComponent implements OnInit {
  // Payment method selection
  paymentMethod: 'credit_card' | 'apple_pay' | 'stc_pay' = 'credit_card';

  // Cost breakdown
  costBreakdown = {
    annualPremium: 10,
    processingFee: 2,
    vat: 1.5,
    total: 13.5
  };

  termsAccepted: boolean = true;
  isAuthenticated: boolean = false;
  isLoading: boolean = false;
  quotaReferenceNo: string = '';
  quotaresponceId: string = '';
  crNumber: string = '';

  paymentDone: boolean = false;
  paymentStatus: string = '';

  // Form models
  vatNumber: string = '';
  noVatNumber: boolean = false;
  showDiscountCode: boolean = false;

  policyHolderDetails = {
    name: '',
    mobile: '',
    email: '',
  };

  @ViewChild('paymentForm') paymentFormElement!: ElementRef;
  paymentFormData: any = {};

  policyHolderWillPay: boolean = false;

  paymentConfirmation = {
    name: '',
    discountCode: '',
    termsAccepted: false,
    medicalDisclosureAcknowledged: false,
  };

  quotationDetail: QuotationDetail | null = null;
  paymentForm: FormGroup;
  selectedPaymentMethod: string = 'credit_card'; // Initialize with default value
  isProcessing: boolean = false;
  errorMessage: string = '';

  orderCurrency: string = 'SAR';
  orderDescription: string = 'Order Payment';
  callbackUrl: string = `${window.location.origin}/payment-callback.html?quotaReferenceNo=${this.quotaReferenceNo}&quotaresponceId=${this.quotaresponceId}`; // Update with your actual callback URL

  // Define months as a property instead of getter to avoid change detection issues
  months: { value: string; label: string }[] = [
    { value: '01', label: '01 - January' },
    { value: '02', label: '02 - February' },
    { value: '03', label: '03 - March' },
    { value: '04', label: '04 - April' },
    { value: '05', label: '05 - May' },
    { value: '06', label: '06 - June' },
    { value: '07', label: '07 - July' },
    { value: '08', label: '08 - August' },
    { value: '09', label: '09 - September' },
    { value: '10', label: '10 - October' },
    { value: '11', label: '11 - November' },
    { value: '12', label: '12 - December' },
  ];
  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private authService: AuthService,
    private quoteService: QuoteService,
    private toastr: ToastrService,
    private paymentService: PaymentService,
    private companyService: CompanyService,
    private policyService: PolicyService,
    private fb: FormBuilder
  ) {
    // CORRECT WAY - Using FormBuilder
    this.paymentForm = this.fb.group({
      paymentType: ['credit_card', [Validators.required]], // Sync validators in array

      // Credit Card Fields - sync validators only
      cardNumber: ['', [Validators.pattern(/^\d{16}$/)]],
      cardHolderName: ['', [Validators.minLength(3)]],
      expiryDate: ['', [Validators.pattern(/^(0[1-9]|1[0-2])\/\d{2}$/)]], // MM/YY
      cvv: ['', [Validators.pattern(/^\d{3,4}$/)]],

      // STC Pay Fields
      stcMobileNumber: ['', [Validators.pattern(/^(05|5)\d{8}$/)]],
      stcBranch: [''],
      stcCashier: [''],
    });
  }

  ngOnInit(): void {
    this.crNumber = this.companyService.getCrNumber();

    const paymentId = this.route.snapshot.queryParams['id'];
    const paymentStatus = this.route.snapshot.queryParams['status'];
    const paymentMessage = this.route.snapshot.queryParams['message'];
    this.quotaReferenceNo =
      this.route.snapshot.queryParams['quotaReferenceNo'] || '';
    this.quotaresponceId =
      this.route.snapshot.queryParams['quotaresponceId'] || '';

    // Fallback: Check sessionStorage if query params are missing (in case redirect lost them)
    if (!this.quotaReferenceNo) {
      this.quotaReferenceNo = sessionStorage.getItem('callback_quotaReferenceNo') || '';
    }
    if (!this.quotaresponceId) {
      this.quotaresponceId = sessionStorage.getItem('callback_quotaresponceId') || '';
    }

    console.log('🔴 ngOnInit - route params:', {
      paymentId,
      paymentStatus,
      paymentMessage,
      quotaReferenceNo: this.quotaReferenceNo,
      quotaresponceId: this.quotaresponceId
    });


    if (paymentId) {
      console.log('🔴 Payment callback detected, blocking other navigation');

      sessionStorage.setItem('processing_payment_callback', 'true');

      this.isLoading = true;
      this.handlePaymentCallback(paymentId, paymentStatus, paymentMessage);
      return;
    }

    this.fetchQuotationDetail();
    this.authService.isAuthenticated$.subscribe((status: boolean) => {
      this.isAuthenticated = status;
    });

    this.paymentForm.get('paymentType')?.valueChanges.subscribe((value) => {
      const previousMethod = this.selectedPaymentMethod;
      this.selectedPaymentMethod = value;

      // Only reset validators, not values during initialization
      this.resetValidators();

      // Only reset non-relevant fields if this is a user-initiated change
      // (not the initial load where previousMethod would be the same as value)
      if (previousMethod && previousMethod !== value) {
        this.resetNonRelevantFields(value);
      }

      if (value === 'credit_card') {
        this.setCreditCardValidators();
      } else if (value === 'stc_pay') {
        this.setStcPayValidators();
      } else if (value === 'apple_pay') {
        // No additional validators needed for Apple Pay
      }
    });

    this.setCreditCardValidators();
  }

  handlePaymentCallback(
    paymentId: string,
    status: string,
    message: string
  ): void {
    this.isLoading = true;

    console.log('Processing payment callback:', { paymentId, status, message });

    this.verifyPaymentOnServer(paymentId, status, message);
  }

  verifyPaymentOnServer(
    paymentId: string,
    clientStatus: string,
    message: string
  ): void {
    this.paymentService
      .verifyPayment(paymentId, this.quotaresponceId)
      .subscribe({
        next: (response) => {
          console.log('Server payment verification response:', response);

          this.isLoading = false;

          sessionStorage.removeItem('processing_payment_callback');

          this.cleanupCallbackUrl();

          if (this.isPaymentValid(response)) {
            this.handleVerifiedPaymentSuccess(response);
          } else {
            this.handlePaymentFailure(
              response.status || clientStatus,
              `Payment status: ${response.status}. ${
                response.source?.message || 'Verification failed.'
              }`
            );
          }
        },
        error: (error) => {
          console.error('Payment verification error:', error);

          this.isLoading = false;

          sessionStorage.removeItem('processing_payment_callback');

          this.cleanupCallbackUrl();

          this.handlePaymentFailure(
            'error',
            'Unable to verify payment. Please contact support with payment ID: ' +
              paymentId
          );
        },
      });
  }

  private cleanupCallbackUrl(): void {
    const url = new URL(window.location.href);

    url.searchParams.delete('id');
    url.searchParams.delete('status');
    url.searchParams.delete('message');

    window.history.replaceState({}, document.title, url.toString());

    console.log('Cleaned up callback URL');
  }

  private isPaymentValid(paymentResponse: any): boolean {
    if (paymentResponse.status == 'success') {
      this.toastr.success('Payment completed successfully!', 'Success', {
        timeOut: 3000,
        progressBar: true,
      });
      this.paymentDone = true;
      this.paymentStatus = 'success';
      return true;
    }

    if (paymentResponse.status == 'failed') {
      console.warn('Payment not completed. Status:', paymentResponse.status);
      return false;
    }

    if (paymentResponse.status == 'amount_mismatch') {
      console.warn('Payment amount mismatch:', {
        expected: this.quotationDetail?.quoteRequest?.amount || 10,
        received: paymentResponse.amount,
      });
      return false;
    }

    return true;
  }

  private handleVerifiedPaymentSuccess(paymentResponse: any): void {
    console.log('Payment verified successfully:', paymentResponse);

    const paymentDetails = {
      id: paymentResponse.id,
      amount: paymentResponse.amount,
      currency: paymentResponse.currency,
      status: paymentResponse.status,
      created: paymentResponse.created_at,
      quotaReferenceNo: this.quotaReferenceNo,
      quotaresponceId: this.quotaresponceId,
    };

    localStorage.setItem('lastPaymentId', paymentResponse.id);
    localStorage.setItem('paymentStatus', 'success');
    localStorage.setItem('paymentDetails', JSON.stringify(paymentDetails));

    this.toastr.success('Payment completed successfully!', 'Success', {
      timeOut: 3000,
      progressBar: true,
    });

    // Call issue-policy API after successful payment
    console.log('🔴 BEFORE calling issuePolicyAfterPayment, quotaReferenceNo:', this.quotaReferenceNo, 'quotationDetail.quoteReferenceNo:', this.quotationDetail?.quoteReferenceNo);
    this.issuePolicyAfterPayment();
    console.log('🟢 AFTER calling issuePolicyAfterPayment');

    setTimeout(() => {
      console.log('Navigating to dashboard after successful payment');
      this.router.navigate(['/dashboard/home'], {
        queryParams: {
          paymentSuccess: true,
          paymentId: paymentResponse.id,
        },
        replaceUrl: true,
      });
    }, 2000);
  }

  private handlePaymentFailure(status: string, message: string): void {
    console.error('Payment failed:', { status, message });

    // Store failure details
    localStorage.setItem('paymentStatus', 'failed');
    localStorage.setItem(
      'lastPaymentError',
      JSON.stringify({
        status,
        message,
        timestamp: new Date().toISOString(),
      })
    );

    // Show error message
    this.toastr.error(
      message || 'Payment failed. Please try again.',
      'Payment Failed',
      {
        timeOut: 5000,
        progressBar: true,
        closeButton: true,
      }
    );

    this.errorMessage = message;

    console.log('Staying on payment page for retry');

    if (this.quotaresponceId) {
      this.fetchQuotationDetail();
    }
  }

  verifyPaymentStatus(paymentId: any, quotaresponceId: string): void {
    this.isLoading = true;
    this.paymentService.verifyPayment(paymentId, quotaresponceId).subscribe({
      next: (response) => {
        this.isLoading = false;
        console.log('Payment verification response:', response);
      },
      error: (error) => {
        this.isLoading = false;
        console.error('Payment verification error:', error);
      },
    });
  }

  selectPaymentMethod(method: 'credit_card' | 'apple_pay' | 'stc_pay'): void {
    this.selectedPaymentMethod = method;
    this.paymentMethod = method;
    this.updateValidators();
  }

  updateValidators(): void {
    Object.keys(this.paymentForm.controls).forEach((key) => {
      this.paymentForm.get(key)?.clearValidators();
      this.paymentForm.get(key)?.updateValueAndValidity();
    });

    if (this.selectedPaymentMethod === 'credit_card') {
      this.setCreditCardValidators();
    } else if (this.selectedPaymentMethod === 'stc_pay') {
      this.setStcPayValidators();
    }
    this.resetNonRelevantFields(this.paymentMethod);

    Object.keys(this.paymentForm.controls).forEach((key) => {
      this.paymentForm.get(key)?.updateValueAndValidity();
    });
  }

  fetchQuotationDetail(): void {
    if (!this.quotaresponceId) {
      this.toastr.error('Quotation details are missing.');
      return;
    }

    this.isLoading = true;

    this.quoteService
      .getQuotationDetail(this.quotaresponceId)
      .pipe(
        catchError((error) => {
          console.error('Error fetching quotation detail:', error);
          this.isLoading = false;
          this.toastr.error('Failed to fetch quotation details.');
          return of(null);
        })
      )
      .subscribe((response: QuotationDetail | null) => {
        if (response) {
          console.log('Quotation detail fetched successfully:', response);
          this.quotationDetail = response;
          this.initCostBreakDown();
          // this.isLoading = false;
        } else {
          this.toastr.error('Quotation details not found.');
        }
      });
  }

  navigateToQuotations() {
    window.history.back();
  }

  private getUserIdFromLocalStorage(): string {
    const keycloakProfile = localStorage.getItem('keycloakUserProfile');
    if (keycloakProfile) {
      try {
        const profile = JSON.parse(keycloakProfile);
        return profile.id;
      } catch (error) {
        console.error('Error parsing Keycloak profile:', error);
        return '';
      }
    }
    return '';
  }

  completePaymentAndNavigateToDashBoard() {
    console.log('Starting payment process...');
    if (!this.isAuthenticated) {
      this.toastr.error('You need to be logged in to proceed with payment.');
      const returnUrl = this.router.url;
      this.authService.login(returnUrl);
      console.log('Redirecting to login...');
      this.authService.isAuthenticated$.subscribe((status: boolean) => {
        if (status) {
          this.isAuthenticated = status;
          this.proceedWithCompanyAssignment();
        }
      });
      return;
    }

    // this.continuePayment();

    this.proceedWithCompanyAssignment();
  }

  private proceedWithCompanyAssignment() {
    const companyId = this.companyService.getCompanyId() || '121';
    const userId = this.getUserIdFromLocalStorage();

    if (!companyId) {
      this.toastr.error('Company information is missing.');
      return;
    }

    if (!userId) {
      this.toastr.error('User information is missing.');
      return;
    }

    this.companyService.assignUserToCompany(companyId, userId).subscribe({
      next: (res) => {
        console.log('User assigned to company successfully:', res);
        this.completePayment();
      },
      error: (err) => {
        console.error('Error assigning user to company:', err);
        this.completePayment();

        this.toastr.error('Failed to assign user to company.');
      },
    });
  }

  // continuePayment() {
  //   console.log('Continuing to payment...');
  //   let activepg = 'moya';

  //   if (activepg === 'moya') {
  //     this.continuePaymentMoyasar();
  //     return;
  //   } else {
  //     if (!this.quotationDetail) return;

  //     this.isLoading = true;

  //     const quoteReq = this.quotationDetail.quoteRequest;

  //     const paymentRequest: PaymentRequest = {
  //       quotaResponceId: this.quotationDetail.id,
  //       items: 'insurance-policy,coverage-extension',
  //       phone: this.policyHolderDetails.mobile || quoteReq?.clientId || '',
  //       email: this.policyHolderDetails.email || '',
  //       street: quoteReq?.entityNameAr || '',
  //       city: this.quotationDetail.entityCrCity || '',
  //       state: '',
  //       zipCode: '',
  //       holderName:
  //         this.policyHolderDetails.name || quoteReq?.companyName || '',
  //       customerAccountCode: quoteReq?.companyId
  //         ? `CUST_${quoteReq.companyId}`
  //         : '',
  //       customerAccountInternalCode: quoteReq?.companyId
  //         ? `INT_CUST_${quoteReq.companyId}`
  //         : '',
  //       transactionCode:
  //         this.quotationDetail.quoteReferenceNo ||
  //         quoteReq?.quoteRequestReferenceNo ||
  //         '',
  //       transactionInternalCode: this.quotationDetail.responseId || '',
  //       itemCode: '',
  //       memo: 'Insurance payment',
  //       orderCode: this.quotationDetail.id,
  //       orderDate: new Date().toISOString(),
  //     };

  //     console.log('Payment request payload:', paymentRequest);

  //     this.paymentService
  //       .initiatePayment(paymentRequest)
  //       .pipe(
  //         catchError((error) => {
  //           console.error('Payment initiation failed:', error);
  //           this.toastr.error('Failed to initiate payment. Please try again.');
  //           return of(null);
  //         }),
  //         finalize(() => {
  //           this.isLoading = false;
  //         })
  //       )
  //       .subscribe((response: any) => {
  //         if (response && response.paymentUrl) {
  //           window.location.href = response.paymentUrl;
  //         } else {
  //           this.toastr.success('Payment initiated successfully!');
  //           this.router.navigate(['/dashboard/home']);
  //         }
  //       });
  //   }
  // }

  // continuePaymentMoyasar() {
  //   if (!this.quotationDetail) return;

  //   this.isLoading = true;
  //   const quoteReq = this.quotationDetail.quoteRequest;

  //   const paymentData = {
  //     amount: this.quotationDetail.quoteRequest?.amount || 100,
  //     description: 'Insurance payment',
  //     method: 'creditcard',
  //     // 'applePay.country': 'SA',
  //     // 'applePay.label':
  //     //   this.policyHolderDetails.name || quoteReq?.companyName || '',
  //     // 'applePay.validate_merchant_url': 'YOUR_MERCHANT_URL',
  //   };

  //   this.paymentFormData = paymentData;

  //   setTimeout(() => {
  //     this.paymentFormElement.nativeElement.submit();
  //   }, 0);
  // }

  formatDate(dateArray: number[] | undefined): string {
    if (!dateArray || dateArray.length < 3) return '';
    const [year, month, day] = dateArray;
    return `${day}/${month}/${year}`;
  }

  getLowestPremium(): number {
    return this.quotationDetail?.quoteRequest?.amount || 10;
  }

  getTotalWithoutTax(): number {
    return this.getLowestPremium();
  }

  getVatAmount(): number {
    return this.getTotalWithoutTax() * 0.15;
  }

  getTotalWithTax(): number {
    return this.getTotalWithoutTax() + this.getVatAmount();
  }

  getShareQuoteLink(): string {
    return `${window.location.origin}/share-quote/${
      this.quotationDetail?.quoteReferenceNo || ''
    }`;
  }

  copyLink(text: string): void {
    navigator.clipboard
      .writeText(text)
      .then(() => {
        this.toastr.success('Link copied to clipboard');
      })
      .catch((err) => {
        console.error('Failed to copy text: ', err);
        this.toastr.error('Failed to copy link');
      });
  }

  resetValidators(): void {
    const allFields = [
      'cardNumber',
      'cardHolderName',
      'expiryDate',
      'cvv',
      'stcMobileNumber',
      'stcBranch',
      'stcCashier',
    ];

    allFields.forEach((field) => {
      const control = this.paymentForm.get(field);
      control?.clearValidators();
      control?.clearAsyncValidators();
      control?.updateValueAndValidity();
    });
  }

  resetNonRelevantFields(currentMethod: string): void {
    // Only reset fields that are not relevant to the current payment method
    let fieldsToReset: string[] = [];

    if (currentMethod === 'credit_card') {
      fieldsToReset = ['stcMobileNumber', 'stcBranch', 'stcCashier'];
    } else if (currentMethod === 'stc_pay') {
      fieldsToReset = ['cardNumber', 'cardHolderName', 'expiryDate', 'cvv'];
    } else if (currentMethod === 'apple_pay') {
      fieldsToReset = [
        'cardNumber', 'cardHolderName', 'expiryDate', 'cvv',
        'stcMobileNumber', 'stcBranch', 'stcCashier'
      ];
    }

    fieldsToReset.forEach((field) => {
      const control = this.paymentForm.get(field);
      control?.reset();
      control?.updateValueAndValidity();
    });
  }

  setCreditCardValidators(): void {
    // Set sync validators using setValidators (not as async)
    this.paymentForm
      .get('cardNumber')
      ?.setValidators([Validators.required, Validators.pattern(/^\d{16}$/)]);

    this.paymentForm
      .get('cardHolderName')
      ?.setValidators([Validators.required, Validators.minLength(3)]);

      this.paymentForm.get('expiryDate')?.setValidators([
        Validators.pattern(/^(0[1-9]|1[0-2])\/\d{2}$/), // MM/YY format
      ]);

    this.paymentForm
      .get('cvv')
      ?.setValidators([Validators.required, Validators.pattern(/^\d{3,4}$/)]);

    // Update validity for all fields
    [
      'cardNumber',
      'cardHolderName',
      'expiryDate',
      'cvv',
    ].forEach((field) => {
      this.paymentForm.get(field)?.updateValueAndValidity();
    });
  }

  setStcPayValidators(): void {
    this.paymentForm
      .get('stcMobileNumber')
      ?.setValidators([
        Validators.required,
        Validators.pattern(/^(05|5)\d{8}$/),
      ]);

    this.paymentForm.get('stcBranch')?.setValidators([Validators.required]);
    this.paymentForm.get('stcCashier')?.setValidators([Validators.required]);

    ['stcMobileNumber', 'stcBranch', 'stcCashier'].forEach((field) => {
      this.paymentForm.get(field)?.updateValueAndValidity();
    });
  }

  isPaymentFormValid(): boolean {
    if (this.selectedPaymentMethod === 'apple_pay') {
      return true; // Apple Pay has its own validation
    }
    return this.paymentForm.valid;
  }

  completePayment(): void {
    if (!this.isPaymentFormValid()) {
      this.paymentForm.markAllAsTouched();
      this.errorMessage = 'Please fill in all required fields correctly.';
      return;
    }

    this.isProcessing = true;
    this.errorMessage = '';

    this.callbackUrl = `${window.location.origin}/payment-callback.html?quotaReferenceNo=${this.quotaReferenceNo}&quotaresponceId=${this.quotaresponceId}`;

    if(this.vatNumber !== '')
    {
      this.updateVatNumber();
    }

    // Prepare payment request payload
    const paymentRequest: PaymentRequestDto = {
      amount: this.quotationDetail?.quoteRequest?.amount || 1000, // Convert to halalas
      currency: this.orderCurrency,
      description: this.orderDescription,
      callback_url: this.callbackUrl,
    };

    // Build payment request based on selected payment method
    switch (this.selectedPaymentMethod) {
      case 'credit_card':
        const [month = '', yy = ''] = (this.paymentForm.get('expiryDate')?.value || '').split('/');
        const year = yy ? `${Math.floor(new Date().getFullYear() / 100)}${yy}` : '';

        paymentRequest.source = {
          type: 'creditcard',
          name: this.paymentForm.get('cardHolderName')?.value,
          number: this.paymentForm.get('cardNumber')?.value,
          cvc: this.paymentForm.get('cvv')?.value,
          month: month,
          year: year,
        };
        break;

      case 'stc_pay':
        paymentRequest.stcPay = {
          type: 'stcpay',
          mobile: this.paymentForm.get('stcMobileNumber')?.value,
          branch: this.paymentForm.get('stcBranch')?.value || 'Branch01',
          cashier: this.paymentForm.get('stcCashier')?.value || 'Cashier01',
        };
        break;

      case 'apple_pay':
        // Apple Pay requires token-based payment
        // You need to implement Apple Pay JS first to get the token
        paymentRequest.tokenSource = {
          type: 'applepay',
          token: 'APPLE_PAY_TOKEN', // This should come from Apple Pay authorization
          manual: false,
        };
        break;
    }

    console.log('Processing payment:', paymentRequest);

    // Call payment service
    this.paymentService
      .createPayment(
        paymentRequest,
        this.getUserIdFromLocalStorage(),
        this.companyService.getCompanyId()
      )
      .subscribe({
        next: (response) => {
          const paymentId = this.route.snapshot.queryParams['id'];
          if (paymentId) {
            this.verifyPaymentStatus(paymentId, this.quotaresponceId);
          }
          console.log('Payment response:', response);
          if (response.status === 'paid') {
            // Payment successful
            this.handlePaymentSuccess(response);
          } else if (response.status === 'initiated') {
            // 3DS authentication required - redirect to authentication URL
            this.handle3DSRedirect(response);
          } else if (response.status === 'authorized') {
            // Payment authorized but needs to be captured
            this.handlePaymentAuthorized(response);
          } else {
            this.errorMessage = 'Payment processing failed. Please try again.';
            this.isProcessing = false;
          }
        },
        error: (error) => {
          console.error('Payment error:', error);
          this.errorMessage =
            error.error?.message ||
            'An error occurred while processing payment.';
          this.isProcessing = false;
        },
      });
  }

  handlePaymentSuccess(response: any): void {
    // Store payment ID for reference
    localStorage.setItem('lastPaymentId', response.id);
    localStorage.setItem('paymentStatus', 'success');

    // Navigate to success page or dashboard
    this.router.navigate(['/payment/success'], {
      queryParams: { paymentId: response.id },
    });
  }

  handle3DSRedirect(response: any): void {
    // For 3DS payments, Moyasar provides a transaction URL
    if (response.source && response.source.transaction_url) {
      window.location.href = response.source.transaction_url;
    }
  }

  handlePaymentAuthorized(response: any): void {
    // Payment is authorized, navigate to confirmation
    this.router.navigate(['/payment/authorized'], {
      queryParams: { paymentId: response.id },
    });
  }

  processApplePayPayment(): void {
    // Initialize Apple Pay session
    // This requires Apple Pay JS setup
    console.log('Initiating Apple Pay...');

    // You'll need to implement Apple Pay JS here
    // Reference: https://developer.apple.com/documentation/apple_pay_on_the_web

    this.errorMessage = 'Apple Pay integration requires additional setup.';
  }

  navigateToDashBoard(): void {
    this.router.navigate(['/dashboard']);
  }

  get currentYear(): number {
    return new Date().getFullYear();
  }

  get years(): number[] {
    return Array.from({ length: 15 }, (_, i) => this.currentYear + i);
  }

  ngOnDestroy(): void {
    // Clean up session flag if component is destroyed
    sessionStorage.removeItem('processing_payment_callback');
  }

  /**
   * Issue policy after successful payment
   */
  private issuePolicyAfterPayment(): void {
    const companyId = this.companyService.getCompanyId();
    const parsedCompanyId = this.resolveCompanyId(companyId);
    const quotaReferenceNo = this.quotaReferenceNo || this.quotationDetail?.quoteReferenceNo || '';

    console.log('🔴 issuePolicyAfterPayment START');
    console.log('  companyId (raw):', companyId);
    console.log('  parsedCompanyId:', parsedCompanyId);
    console.log('  quotaReferenceNo:', quotaReferenceNo);
    console.log('  this.quotaReferenceNo:', this.quotaReferenceNo);
    console.log('  this.quotationDetail?.quoteReferenceNo:', this.quotationDetail?.quoteReferenceNo);

    if (!parsedCompanyId || !quotaReferenceNo) {
      console.warn('🔴 EARLY EXIT: Missing companyId or quotaReferenceNo for policy issuance', { parsedCompanyId, quotaReferenceNo });
      return;
    }

    const issuePolicyPayload: IssuePolicyRequest = {
      companyId: parsedCompanyId,
      quotaReferenceNo
    };

    console.log('✅ issuePolicyAfterPayment PROCEEDING with payload:', issuePolicyPayload);

    this.policyService
      .issuePolicy(issuePolicyPayload)
      .pipe(
        take(1),
        catchError((error) => {
          console.error('❌ Failed to issue policy:', error);
          this.toastr.warning(
            'Payment successful, but policy issuance failed. Please contact support.',
            'Warning'
          );
          return of(null);
        })
      )
      .subscribe((response) => {
        if (response) {
          console.log('✅ Policy issued successfully:', response);
          this.toastr.success('Policy issued successfully!', 'Success');
        } else {
          console.warn('⚠️ Policy issuance returned null or falsy response');
        }
      });
  }

  private resolveCompanyId(companyId: unknown): number | null {
    if (companyId === null || companyId === undefined) {
      return null;
    }

    if (typeof companyId === 'number') {
      return Number.isFinite(companyId) ? companyId : null;
    }

    if (typeof companyId === 'string') {
      const normalized = companyId.replace(/"/g, '').trim();
      if (!normalized) {
        return null;
      }

      const numeric = Number(normalized);
      if (!Number.isNaN(numeric) && numeric !== 0) {
        return numeric;
      }

      try {
        const parsed = JSON.parse(companyId);
        return this.resolveCompanyId(parsed);
      } catch (error) {
        console.warn('Unable to parse companyId string:', companyId, error);
        return null;
      }
    }

    if (typeof companyId === 'object') {
      const valueWithId = companyId as { id?: number | string };
      if (valueWithId && valueWithId.id !== undefined) {
        return this.resolveCompanyId(valueWithId.id as unknown);
      }
    }

    return null;
  }

  calculateCosts(): void {
    const { annualPremium, processingFee } = this.costBreakdown;
    const subtotal = annualPremium + processingFee;
    this.costBreakdown.vat = Math.round(subtotal * 0.15);
    this.costBreakdown.total = subtotal + this.costBreakdown.vat;
  }

  isFormValid(): boolean {
    switch (this.paymentMethod) {
      case 'credit_card':
        return this.isPaymentFormValid()
      case 'apple_pay':
        return true;
      case 'stc_pay':
        return this.isPaymentFormValid()
      default:
        return false;
    }
  }

  getPaymentButtonText(): string {
    switch (this.paymentMethod) {
      case 'credit_card':
        return 'PAYMENT.COMPLETE_PAYMENT';
      case 'apple_pay':
        return 'PAYMENT.PAY_WITH_APPLE_PAY';
      case 'stc_pay':
        return 'PAYMENT.PAY_VIA_STC_PAY';
      default:
        return 'PAYMENT.COMPLETE_PAYMENT';
    }
  }

  initCostBreakDown(): void {
    // base amount from quotation details
    const annualPremium = this.quotationDetail?.quoteRequest?.amount ?? 0;

    // example: processing fee as 2% of premium
    const processingFee = annualPremium * 0.02;

    // VAT as 15% of (premium + processing fee)
    const vatRate = 0.15;
    const vat = (annualPremium + processingFee) * vatRate;

    // total = premium + processing fee + VAT
    const total = annualPremium + processingFee + vat;

    // store in component variable
    this.costBreakdown = {
      annualPremium,
      processingFee,
      vat,
      total,
    };
  }

  toggleVatValidation(): void {
    if (this.noVatNumber) {
      this.vatNumber = ''; // clear field when disabled
    }
  }

  updateVatNumber(): void
  {
    let companyData;
    if(this.quotationDetail?.quoteRequest?.companyId)
    {
      this.companyService.getCompanyByCompanyId(this.quotationDetail?.quoteRequest?.companyId.toString()).subscribe({
        next: (company) => {
          console.log('Company data loaded:', company);

          // Format the policy start date for the date input
          companyData = {
            ...company
          };
          companyData.vatNumber = this.vatNumber;

          this.companyService
          .updateCompany(companyData?.id ?? 0, companyData, this.getUserIdFromLocalStorage())
          .subscribe({
            next: (response) => {
              console.log('Company update successful:', response);
            },
            error: (err) => {
              console.error('Company update failed:', err);
            },
      });
        },
        error: (err) => {
          console.error('Failed to load company data:', err);
          this.toastr.error('Failed to load company data');
          return;
        },
      });
    }
  }

  getProviderAvatarText(name: string | null): string {
    if (!name) return '';
    const parts = name.trim().split(' ').filter(p => p.length > 0);

    if (parts.length === 0) return '';
    if (parts.length === 1) {
      return (parts[0][0] || '') + (parts[0][1] || '');
    }

    return (parts[0][0] || '') + (parts[1][0] || '');
  }
}
